// Suscripción Web Push (§14). El permiso se pide solo desde una acción del usuario.
import { supabase } from '../data/supabase.ts';
import { isIos, isStandalone } from './store.ts';

export type PushAvailability = 'supported' | 'unsupported' | 'ios-install' | 'not-configured';

export function pushAvailability(): PushAvailability {
  if (!import.meta.env.VITE_VAPID_PUBLIC_KEY) return 'not-configured';
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (isIos() && !isStandalone()) return 'ios-install';
  return supported ? 'supported' : 'unsupported';
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(base64url.length / 4) * 4, '=');
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

export async function enablePush(): Promise<'enabled' | 'denied' | 'error'> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(import.meta.env.VITE_VAPID_PUBLIC_KEY ?? ''),
      }));
    const json = subscription.toJSON();
    const { error } = await supabase.rpc('register_web_push', {
      p_endpoint: subscription.endpoint,
      p_p256dh: json.keys?.['p256dh'] ?? '',
      p_auth: json.keys?.['auth'] ?? '',
      p_user_agent: navigator.userAgent.slice(0, 500),
    });
    if (error) throw error;
    return 'enabled';
  } catch {
    return 'error';
  }
}

/** Retira el destino de este dispositivo (al desactivar o al cerrar sesión). */
export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription().catch(() => null);
  if (!subscription) return;
  await supabase.rpc('unregister_web_push', { p_endpoint: subscription.endpoint });
  await subscription.unsubscribe().catch(() => undefined);
}
